import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { COMPANY } from "../i18n/translations";

const LangSwitch = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center border border-white/15" data-testid="lang-switch">
      <button
        data-testid="lang-switch-ro"
        onClick={() => setLang("ro")}
        className={`px-2.5 py-1 text-xs font-bold tracking-widest uppercase transition-colors ${
          lang === "ro"
            ? "bg-[#FF4500] text-black"
            : "text-white/70 hover:text-white"
        }`}
      >
        RO
      </button>
      <span className="h-4 w-px bg-white/15" />
      <button
        data-testid="lang-switch-en"
        onClick={() => setLang("en")}
        className={`px-2.5 py-1 text-xs font-bold tracking-widest uppercase transition-colors ${
          lang === "en"
            ? "bg-[#FF4500] text-black"
            : "text-white/70 hover:text-white"
        }`}
      >
        EN
      </button>
    </div>
  );
};

const Header = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home, testid: "nav-home" },
    { to: "/services", label: t.nav.services, testid: "nav-services" },
    { to: "/about", label: t.nav.about, testid: "nav-about" },
    { to: "/clients", label: t.nav.clients, testid: "nav-clients" },
    { to: "/contact", label: t.nav.contact, testid: "nav-contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-[#050505]/95 backdrop-blur-sm border-b border-white/10"
    >
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 flex items-center justify-between h-16 lg:h-20">
        <Link
          to="/"
          data-testid="brand-link"
          className="flex items-center gap-3 group h-12 lg:h-14"
        >
          <img
            src="/logo.svg"
            alt="Conveyor Belts S.R.L."
            className="h-full w-auto object-contain transition-transform group-hover:scale-105"
          />
          <span className="font-display uppercase tracking-tight text-white group-hover:text-[#FF4500] transition-colors leading-none text-[2rem] lg:text-[2.5rem] whitespace-nowrap">
            Conveyor Belts <span className="text-white/50">S.R.L.</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              end={l.to === "/"}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium uppercase tracking-wider transition-colors ${
                  isActive
                    ? "text-[#FF4500]"
                    : "text-white/80 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LangSwitch />
          <a
            href={`tel:${COMPANY.phoneDial}`}
            data-testid="header-call-btn"
            className="hidden md:inline-flex items-center gap-2 bg-[#FF4500] hover:bg-[#FF5722] text-black px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors"
          >
            <Phone className="w-4 h-4" />
            {COMPANY.phone}
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden text-white p-2 border border-white/15"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-white/10 bg-[#050505]"
        >
          <div className="px-5 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-${l.testid}`}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `px-3 py-3 text-sm font-medium uppercase tracking-wider border-b border-white/5 ${
                    isActive ? "text-[#FF4500]" : "text-white/80"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={`tel:${COMPANY.phoneDial}`}
              data-testid="mobile-call-btn"
              className="mt-3 inline-flex items-center justify-center gap-2 bg-[#FF4500] text-black px-4 py-3 text-sm font-bold uppercase tracking-wider"
            >
              <Phone className="w-4 h-4" />
              {COMPANY.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = () => {
  const { t } = useLang();
  return (
    <footer
      data-testid="site-footer"
      className="border-t border-white/10 bg-[#050505] mt-24"
    >
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-5 h-16">
            <img
              src="/logo.svg"
              alt="Conveyor Belts S.R.L."
              className="h-full w-auto object-contain"
            />
            <span className="font-display uppercase tracking-tight text-white leading-none text-[2.75rem] whitespace-nowrap">
              Conveyor Belts <span className="text-white/50">S.R.L.</span>
            </span>
          </div>
          <p className="text-white/60 max-w-md text-sm leading-relaxed">
            {t.common.tagline}. Făurei, Brăila — România.
          </p>
        </div>

        <div>
          <p className="text-overline text-[#FF4500] mb-4">{t.footer.nav}</p>
          <ul className="space-y-2 text-sm">
            <li>
              <Link to="/" className="text-white/70 hover:text-white" data-testid="footer-link-home">
                {t.nav.home}
              </Link>
            </li>
            <li>
              <Link to="/services" className="text-white/70 hover:text-white" data-testid="footer-link-services">
                {t.nav.services}
              </Link>
            </li>
            <li>
              <Link to="/about" className="text-white/70 hover:text-white" data-testid="footer-link-about">
                {t.nav.about}
              </Link>
            </li>
            <li>
              <Link to="/clients" className="text-white/70 hover:text-white" data-testid="footer-link-clients">
                {t.nav.clients}
              </Link>
            </li>
            <li>
              <Link to="/contact" className="text-white/70 hover:text-white" data-testid="footer-link-contact">
                {t.nav.contact}
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <p className="text-overline text-[#FF4500] mb-4">{t.footer.contact}</p>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2 text-white/80">
              <Phone className="w-4 h-4 mt-0.5 text-[#FF4500]" />
              <a href={`tel:${COMPANY.phoneDial}`} className="hover:text-white" data-testid="footer-phone">
                {COMPANY.phone}
              </a>
            </li>
            <li className="flex items-start gap-2 text-white/80">
              <Mail className="w-4 h-4 mt-0.5 text-[#FF4500]" />
              <a href={`mailto:${COMPANY.email}`} className="hover:text-white" data-testid="footer-email">
                {COMPANY.email}
              </a>
            </li>
            <li className="flex items-start gap-2 text-white/80">
              <MapPin className="w-4 h-4 mt-0.5 text-[#FF4500]" />
              <span>{COMPANY.address}</span>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-2 text-xs text-white/40 uppercase tracking-widest">
          <span>© {new Date().getFullYear()} {t.footer.cui}</span>
          <span className="flex items-center gap-2">
            {t.footer.rights}
            <ArrowUpRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </footer>
  );
};

const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col bg-[#050505]">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};

export default Layout;
